/**
 * Who may take a card payment by typing the number in.
 *
 * One rule, in one place, because three things have to agree about it and
 * disagreement is expensive in both directions. The server has to refuse a
 * charge from somebody who should not be taking one. The field app has to
 * decide whether to draw the button at all — and a technician who is shown a
 * button that then refuses them has been told, by the app, that the office
 * does not trust them, in front of the customer. The office app and the web
 * team screen have to describe the same rule back to the admin setting it.
 *
 * The rule:
 *
 *   - Admins and dispatchers can always take a card. They run the business;
 *     withdrawing it from the person who connected the bank account would be
 *     a setting whose only use is locking yourself out.
 *   - A technician can only when an admin has granted it on their user.
 *     Off by default, because the default for "can move this customer's money"
 *     should be no, and because a tech hired to clear drains should not
 *     acquire a card terminal by being added to the team.
 *
 * Deliberately NOT a company-wide switch. Field crews are not uniform: the
 * lead on a van takes payment and the apprentice riding along does not, and
 * that distinction is per person or it is nothing.
 */
/** Office roles carry it with the job; nobody grants it to them. */
export function cardPermissionIsInherent(role) {
    return role === 'admin' || role === 'dispatcher';
}
/** Whether this person may charge a card they have typed in. */
export function canTakeCardPayment(user) {
    if (cardPermissionIsInherent(user.role))
        return true;
    return user.canTakePayment === true;
}
/**
 * Why the button is not there, in a sentence a technician can act on.
 *
 * Returns null when they can take a card. Every other answer names the one
 * thing standing in the way, because "payments unavailable" at a customer's
 * door is worse than no button at all — it leaves the tech guessing whether
 * to wait, retry, or phone the office.
 */
export function cardPaymentBlocker(input) {
    if (!canTakeCardPayment(input.user)) {
        return 'The office has not set this phone up to take cards. They can turn it on for you under Team.';
    }
    /*
     * Signal comes before the processor and the account, and after the
     * permission, because that is the order in which the caller actually knows
     * these things.
     *
     * The permission rides on the session, so it is known in a basement. The
     * other two are answered by the server, so offline they are not false —
     * they are unknown, and the field app passes false for want of anything
     * better. Reporting "this company only takes cards by link" to a technician
     * standing in a cellar would be a lie told confidently, and they would
     * believe it and stop trying.
     */
    if (!input.online) {
        return 'Taking a card needs a connection. Nothing is queued, so the customer is never charged twice.';
    }
    if (!input.providerSupportsTypedCards) {
        return 'This company takes cards by sending the customer a link rather than typing the number in.';
    }
    if (!input.accountReady) {
        return 'The office has not finished connecting the company card account yet.';
    }
    return null;
}
/**
 * Whether this person can be given a job.
 *
 * A technician always can. An office role can when somebody has ticked
 * "goes on jobs" for them — which is the common case in a small contractor,
 * where the owner is often the one on the roof.
 *
 * It lives here, beside the other rule about what a person may do, for the
 * same reason that one does: the technician picker, the dispatch board and
 * the server all have to answer this identically, and three copies of
 * `role === 'tech' || goesOnJobs` is three chances to answer it differently.
 */
export function isAssignable(user) {
    if (user.active === false)
        return false;
    return user.role === 'tech' || user.goesOnJobs === true;
}
//# sourceMappingURL=card-permission.js.map